import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  ArrowLeft,
  Search,
  UserPlus,
  X,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Check,
  Users,
  UserCheck,
  Layers,
  ChevronUp,
  ChevronsUpDown,
} from 'lucide-react';

const STATUS_COLORS: Record<string, string> = {
  SUBSCRIBED:   'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  UNSUBSCRIBED: 'bg-red-500/10 text-red-500',
  BOUNCED:      'bg-orange-500/10 text-orange-500',
  COMPLAINED:   'bg-purple-500/10 text-purple-500',
  INVALID:      'bg-slate-100 dark:bg-white/5 text-slate-400',
};

const PAGE_SIZE = 50;

const listSchema = z.object({
  name: z.string().min(1, 'List name is required'),
  description: z.string().optional(),
});
type ListFormValues = z.infer<typeof listSchema>;

export const ListDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Pagination + filters
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortKey, setSortKey] = useState('email');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Add member
  const [addSearch, setAddSearch] = useState('');
  const [debouncedAddSearch, setDebouncedAddSearch] = useState('');

  // Inline edit name/description
  const [editing, setEditing] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; message: string; onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const openConfirm = (title: string, message: string, onConfirm: () => void) =>
    setConfirmDialog({ open: true, title, message, onConfirm });
  const closeConfirm = () => setConfirmDialog(d => ({ ...d, open: false }));

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedAddSearch(addSearch), 300);
    return () => clearTimeout(t);
  }, [addSearch]);

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  // Fetch list metadata
  const { data: list, isLoading: listLoading } = useQuery({
    queryKey: ['list', id],
    queryFn: async () => {
      const res = await apiClient.get(`/lists/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<ListFormValues>({
    resolver: zodResolver(listSchema),
  });

  useEffect(() => {
    if (list) {
      setValue('name', list.name);
      setValue('description', list.description || '');
    }
  }, [list, setValue]);

  // Fetch paginated members
  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['listMembers', id, page, debouncedSearch, statusFilter, sortKey, sortDir],
    queryFn: async () => {
      const res = await apiClient.get('/recipients', {
        params: {
          listId: id,
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          status: statusFilter || undefined,
          sortBy: sortKey,
          sortDir,
        },
      });
      return res.data;
    },
    enabled: !!id,
    keepPreviousData: true,
  });

  const members: any[] = membersData?.recipients || [];
  const totalMembers: number = membersData?.pagination?.total || 0;
  const totalPages = Math.ceil(totalMembers / PAGE_SIZE);

  // Search recipients not in this list (for add)
  const { data: searchData } = useQuery({
    queryKey: ['recipientSearch', debouncedAddSearch],
    queryFn: async () => {
      const res = await apiClient.get('/recipients', {
        params: { search: debouncedAddSearch, limit: 20, status: 'SUBSCRIBED' },
      });
      return res.data;
    },
    enabled: debouncedAddSearch.length >= 2,
  });
  const searchResults: any[] = (searchData?.recipients || []).filter(
    (r: any) => !members.some((m: any) => (m._id || m.id) === (r._id || r.id))
  );

  // Save list details
  const saveMutation = useMutation({
    mutationFn: async (data: ListFormValues) => {
      const res = await apiClient.put(`/lists/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['list', id]);
      queryClient.invalidateQueries(['lists']);
      setEditing(false);
      showToast('List updated', 'success');
    },
    onError: () => showToast('Failed to update list', 'error'),
  });

  // Add member
  const addMutation = useMutation({
    mutationFn: async (recipientId: string) => {
      await apiClient.post('/recipients/bulk-add-list', { recipientIds: [recipientId], listId: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['listMembers', id]);
      queryClient.invalidateQueries(['lists']);
      setAddSearch('');
      showToast('Member added', 'success');
    },
    onError: () => showToast('Failed to add member', 'error'),
  });

  // Remove member
  const removeMutation = useMutation({
    mutationFn: async (recipientId: string) => {
      await apiClient.delete(`/lists/${id}/recipients/${recipientId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['listMembers', id]);
      queryClient.invalidateQueries(['lists']);
    },
    onError: () => showToast('Failed to remove member', 'error'),
  });

  if (listLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!list) {
    return (
      <div className="py-24 text-center text-slate-400">
        List not found.{' '}
        <button onClick={() => navigate('/lists')} className="text-primary-500 hover:underline">Go back</button>
      </div>
    );
  }

  const pct = list.totalRecipients > 0
    ? Math.round((list.subscribedRecipients / list.totalRecipients) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate('/lists')}
          className="mt-0.5 p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-colors shrink-0"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="flex-1 min-w-0">
          {editing ? (
            <form onSubmit={handleSubmit(d => saveMutation.mutate(d))} className="space-y-3">
              <div>
                <input
                  {...register('name')}
                  autoFocus
                  className="form-input text-lg font-bold w-full max-w-md"
                  placeholder="List name"
                />
                {errors.name && <span className="text-xs text-accent-rose">{errors.name.message}</span>}
              </div>
              <input
                {...register('description')}
                className="form-input text-sm w-full max-w-md"
                placeholder="Description (optional)"
              />
              <div className="flex gap-2">
                <button type="submit" disabled={saveMutation.isLoading} className="btn-primary flex items-center gap-1.5 text-xs px-3 py-1.5">
                  <Check size={13} /> Save
                </button>
                <button type="button" onClick={() => setEditing(false)} className="btn-secondary text-xs px-3 py-1.5">
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center text-primary-500 shrink-0 mt-0.5">
                <Layers size={18} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase truncate">
                    {list.name}
                  </h2>
                  <button
                    onClick={() => setEditing(true)}
                    className="p-1 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-colors shrink-0"
                    title="Edit name"
                  >
                    <Edit3 size={13} />
                  </button>
                </div>
                {list.description && (
                  <p className="text-xs text-slate-400 mt-0.5">{list.description}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-6 shrink-0">
          <div className="text-center">
            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300 justify-center">
              <Users size={14} className="text-slate-400" />
              <span className="text-lg font-bold tabular-nums">{list.totalRecipients || 0}</span>
            </div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Total</div>
          </div>
          <div className="text-center">
            <div className="flex items-center gap-1.5 text-emerald-500 justify-center">
              <UserCheck size={14} />
              <span className="text-lg font-bold tabular-nums">{list.subscribedRecipients || 0}</span>
            </div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Active</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold tabular-nums text-slate-700 dark:text-slate-200">{pct}%</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Health</div>
          </div>
        </div>
      </div>

      {/* Add member */}
      <div className="glass-panel rounded-2xl p-4">
        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-2">Add Member</label>
        <div className="relative max-w-sm">
          <UserPlus size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by email or name..."
            value={addSearch}
            onChange={e => setAddSearch(e.target.value)}
            className="form-input pl-8 pr-3 py-2 text-sm w-full"
          />
        </div>
        {debouncedAddSearch.length >= 2 && (
          <div className="mt-1 rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden bg-white dark:bg-slate-900 max-w-sm max-h-48 overflow-y-auto">
            {searchResults.length === 0 ? (
              <div className="px-4 py-3 text-xs text-slate-400">No results or already in list</div>
            ) : searchResults.map((r: any) => (
              <div key={r._id || r.id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                <div>
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{r.email}</div>
                  {(r.firstName || r.lastName) && (
                    <div className="text-xs text-slate-400">{r.firstName} {r.lastName}</div>
                  )}
                </div>
                <button
                  onClick={() => addMutation.mutate(r._id || r.id)}
                  disabled={addMutation.isLoading}
                  className="text-xs btn-primary px-2.5 py-1 shrink-0 ml-3"
                >
                  Add
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Members table */}
      <div className="glass-panel rounded-2xl overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 py-3 border-b border-slate-200 dark:border-white/5">
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Members
            {!membersLoading && (
              <span className="ml-1.5 normal-case font-normal text-slate-500">
                ({totalMembers.toLocaleString()})
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              className="form-input py-1.5 text-xs bg-white dark:bg-slate-900 pr-7"
            >
              <option value="">All Statuses</option>
              <option value="SUBSCRIBED">Subscribed</option>
              <option value="UNSUBSCRIBED">Unsubscribed</option>
              <option value="BOUNCED">Bounced</option>
              <option value="COMPLAINED">Complained</option>
              <option value="INVALID">Invalid</option>
            </select>
            {/* Search */}
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search members..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="form-input pl-7 pr-3 py-1.5 text-xs w-44"
              />
            </div>
          </div>
        </div>

        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 dark:border-white/5">
              {[
                { key: 'email', label: 'Email', cls: 'text-left px-5 py-3' },
                { key: 'firstName', label: 'Name', cls: 'text-left px-4 py-3' },
                { key: 'status', label: 'Status', cls: 'text-center px-4 py-3' },
                { key: 'createdAt', label: 'Added', cls: 'text-right px-4 py-3' },
              ].map(col => (
                <th
                  key={col.key}
                  className={`${col.cls} text-[11px] font-semibold text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-300 transition-colors`}
                  onClick={() => toggleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {sortKey === col.key
                      ? <ChevronUp size={12} className={sortDir === 'desc' ? 'rotate-180 transition-transform' : 'transition-transform'} />
                      : <ChevronsUpDown size={12} className="opacity-40" />}
                  </span>
                </th>
              ))}
              <th className="text-right px-5 py-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {membersLoading ? (
              <tr>
                <td colSpan={5} className="py-12 text-center">
                  <div className="w-7 h-7 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : members.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-sm text-slate-400">
                  No members found.
                </td>
              </tr>
            ) : members.map((m: any) => {
              const rid = m._id || m.id;
              return (
                <tr key={rid} className="group hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-3 text-sm text-slate-700 dark:text-slate-200 font-medium">
                    {m.email}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                    {[m.firstName, m.lastName].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[m.status] || STATUS_COLORS.INVALID}`}>
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-slate-400 whitespace-nowrap">
                    {new Date(m.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => openConfirm(
                        'Remove from List',
                        `Remove ${m.email} from this list?`,
                        () => removeMutation.mutate(rid)
                      )}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-slate-400 hover:text-accent-rose hover:bg-slate-100 dark:hover:bg-white/5 transition-all"
                      title="Remove from list"
                    >
                      <X size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-white/5">
            <span className="text-xs text-slate-400">
              Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, totalMembers)} of {totalMembers.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              {/* Page numbers — show up to 5 around current */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce<(number | '...')[]>((acc, p, i, arr) => {
                  if (i > 0 && typeof arr[i - 1] === 'number' && (p as number) - (arr[i - 1] as number) > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-1 text-slate-400 text-xs">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p as number)}
                      className={`min-w-[28px] h-7 rounded-lg text-xs font-medium transition-colors ${
                        page === p
                          ? 'bg-primary-500 text-white'
                          : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-700 dark:hover:text-white'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Remove"
        onConfirm={() => { confirmDialog.onConfirm(); closeConfirm(); }}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default ListDetail;
